import React from 'react';
import { Link } from 'react-router-dom';
import { Star, Shield, MessageCircle, Image, UserPlus, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

interface PremiumUpgradeOverlayProps {
  onClose: () => void;
  listingTitle?: string;
}

const PremiumUpgradeOverlay: React.FC<PremiumUpgradeOverlayProps> = ({ onClose, listingTitle }) => {
  const { isAuthenticated } = useAuth();

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const features = [
    {
      icon: <Image className="h-4 w-4" />,
      title: 'Full Photo Gallery',
      description: 'View all property photos in HD'
    },
    {
      icon: <MessageCircle className="h-4 w-4" />,
      title: 'Direct Contact',
      description: 'Message owners instantly'
    },
    {
      icon: <Shield className="h-4 w-4" />,
      title: 'Verified Listings',
      description: 'Access trusted properties'
    }
  ];

  return (
    <div 
      className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50"
      onClick={handleBackdropClick}
    >
      <div className="bg-white rounded-xl max-w-sm w-full overflow-hidden shadow-2xl animate-fadeIn relative">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 p-1.5 hover:bg-gray-100 rounded-full transition-colors"
        >
          <X className="h-4 w-4 text-gray-500" />
        </button>

        {/* Header */}
        <div className="px-5 pt-8 pb-6 text-center">
          <div className="inline-flex items-center justify-center bg-rose-100 rounded-full p-2 mb-3">
            <Star className="h-5 w-5 text-rose-600" />
          </div>
          <h2 className="text-xl font-semibold text-gray-900 mb-1">
            {isAuthenticated ? 'Unlock Premium Access' : 'Sign up to continue'}
          </h2>
          {listingTitle && (
            <p className="text-sm text-gray-600">
              to view full details for <span className="font-medium">{listingTitle}</span>
            </p>
          )}
        </div>

        {/* Features */}
        <div className="px-5 pb-6">
          <div className="space-y-3">
            {features.map((feature, index) => (
              <div 
                key={index}
                className="flex items-start p-3 bg-gray-50 rounded-lg"
              >
                <div className="flex-shrink-0">
                  <div className="inline-flex items-center justify-center bg-white rounded-md p-1.5">
                    {feature.icon}
                  </div>
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-gray-900">
                    {feature.title}
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {feature.description}
                  </p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Actions */}
        <div className="px-5 pb-5">
          {isAuthenticated ? (
            <Link
              to="/subscribe"
              className="block w-full bg-gradient-to-r from-rose-500 to-rose-600 text-white px-4 py-2.5 rounded-lg text-center font-medium hover:from-rose-600 hover:to-rose-700 transition-all text-sm"
            >
              Upgrade Now
            </Link>
          ) : (
            <div className="space-y-2">
              <Link
                to="/register"
                className="flex items-center justify-center w-full bg-gradient-to-r from-rose-500 to-rose-600 text-white px-4 py-2.5 rounded-lg font-medium hover:from-rose-600 hover:to-rose-700 transition-all text-sm"
              >
                <UserPlus className="h-4 w-4 mr-2" />
                Continue with Email
              </Link>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-gray-200"></div>
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="px-2 bg-white text-gray-500">or</span>
                </div>
              </div>
              <Link
                to="/login"
                className="block w-full bg-white text-gray-700 px-4 py-2.5 rounded-lg text-center font-medium border border-gray-300 hover:bg-gray-50 transition-colors text-sm"
              >
                Log in
              </Link>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 bg-gray-50 border-t border-gray-100">
          <p className="text-xs text-gray-500 text-center">
            By continuing, you agree to our{' '}
            <Link to="/terms" className="text-gray-900 hover:underline">Terms</Link>
            {' '}and{' '}
            <Link to="/privacy" className="text-gray-900 hover:underline">Privacy Policy</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default PremiumUpgradeOverlay;